import { api, ApiError } from './api'
import { ulid } from './ulid'

/**
 * Offline submission queue.
 *
 * A worker standing next to a broken machine must be able to report it whether
 * or not the plant wifi reaches that corner. The submission is written to the
 * device first and sent when a connection actually exists.
 *
 * IndexedDB, not localStorage: photos and video are Blobs, and localStorage
 * holds only strings and caps out around 5 MB. IndexedDB stores Blobs natively
 * and has room for real media.
 *
 * Every queued item carries a ULID minted before the first send attempt. That
 * value is the server's idempotency key, so the genuinely dangerous case — a
 * request that succeeds and whose response is lost — resolves to the existing
 * submission instead of a duplicate.
 */

const DB_NAME = 'kapro-offline'
const DB_VERSION = 1
const STORE = 'submissions'

export type QueueStatus = 'pending' | 'failed'

export interface QueuedSubmission {
  /** ULID. Also the idempotency key sent to the server. */
  id: string
  /** Whose submission — one device may be shared between shifts. */
  userId: string
  payload: {
    workCenterId: string
    type: string
    priority: string
    title?: string
    description?: string
    improvement?: Record<string, string>
  }
  files: File[]
  /**
   * The spoken message, if one was recorded, and how long it runs.
   *
   * Its own field rather than another entry in `files`, matching the API: the
   * duration has to travel with the recording, and a parallel array of durations
   * indexed against `files` is a pairing that only has to slip once.
   *
   * Optional because records written before voice messages existed are still
   * sitting in people's IndexedDB — a queued report survives app updates, which
   * is the entire point of it.
   */
  voiceNote?: { file: File; durationSec: number } | null
  /** Denormalised for the queue list, which must render with no network. */
  workCenterName: string
  typeLabel: string
  createdAt: number
  attempts: number
  lastAttemptAt: number | null
  lastError: string | null
  status: QueueStatus
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb()
  const transaction = db.transaction(STORE, mode)
  const result = await promisify(fn(transaction.objectStore(STORE)))

  // Wait for the transaction itself, not just the request — a write is not
  // durable until the transaction commits.
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })

  return result
}

// ---------------------------------------------------------------------------
// Change notification
// ---------------------------------------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify(): void {
  listeners.forEach((listener) => listener())
}

// ---------------------------------------------------------------------------
// Queue operations
// ---------------------------------------------------------------------------

export async function listQueue(userId?: string): Promise<QueuedSubmission[]> {
  try {
    const all = await withStore('readonly', (store) => store.getAll() as IDBRequest<QueuedSubmission[]>)

    return all
      .filter((item) => !userId || item.userId === userId)
      // ULIDs sort by creation time, so this is chronological.
      .sort((a, b) => a.id.localeCompare(b.id))
  } catch {
    // A browser with IndexedDB blocked (private mode in some engines) must not
    // take the whole app down — it simply has no queue.
    return []
  }
}

export async function enqueue(
  item: Omit<QueuedSubmission, 'id' | 'attempts' | 'lastAttemptAt' | 'lastError' | 'status' | 'createdAt'>,
): Promise<QueuedSubmission> {
  const record: QueuedSubmission = {
    ...item,
    id: ulid(),
    createdAt: Date.now(),
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
    status: 'pending',
  }

  try {
    await withStore('readwrite', (store) => store.put(record))
  } catch (error) {
    // Photos are large. If the device is out of room, say so plainly instead of
    // silently losing the report.
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error(
        'Nema dovoljno prostora na uređaju za čuvanje prijave. Obrišite nešto iz reda čekanja ili pošaljite bez priloga.',
      )
    }
    throw error
  }

  notify()
  return record
}

export async function removeFromQueue(id: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(id))
  notify()
}

async function update(item: QueuedSubmission): Promise<void> {
  await withStore('readwrite', (store) => store.put(item))
  notify()
}

/** Move a permanently failed item back into the queue after the user edits it. */
export async function retryNow(id: string): Promise<void> {
  const item = await withStore('readonly', (store) => store.get(id) as IDBRequest<QueuedSubmission>)
  if (!item) return

  await update({ ...item, status: 'pending', attempts: 0, lastAttemptAt: null, lastError: null })
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

/**
 * Exponential backoff, capped at five minutes.
 *
 * Without this an item that keeps failing would retry on every online event and
 * every interval tick, which on a flaky connection means a phone burning
 * battery on a request that cannot succeed.
 */
function nextAttemptAt(item: QueuedSubmission): number {
  if (!item.lastAttemptAt) return 0

  const delay = Math.min(5000 * 2 ** item.attempts, 5 * 60 * 1000)
  return item.lastAttemptAt + delay
}

function buildFormData(item: QueuedSubmission): FormData {
  const form = new FormData()

  form.append('clientUuid', item.id)
  form.append('workCenterId', item.payload.workCenterId)
  form.append('type', item.payload.type)
  form.append('priority', item.payload.priority)

  if (item.payload.title) form.append('title', item.payload.title)
  if (item.payload.description) form.append('description', item.payload.description)

  if (item.payload.improvement) {
    for (const [key, value] of Object.entries(item.payload.improvement)) {
      if (value) form.append(`improvement[${key}]`, value)
    }
  }

  item.files.forEach((file) => form.append('files[]', file))

  if (item.voiceNote) {
    form.append('voiceNote', item.voiceNote.file)
    form.append('voiceNoteDurationSec', String(item.voiceNote.durationSec))
  }

  return form
}

// One flush at a time. Two concurrent passes would send every item twice; the
// server would dedupe them, but the uploads would still cost the worker's data.
let flushing = false

export interface FlushResult {
  sent: number
  failed: number
  remaining: number
}

export async function flushQueue(userId: string): Promise<FlushResult> {
  if (flushing || !navigator.onLine) {
    return { sent: 0, failed: 0, remaining: (await listQueue(userId)).length }
  }

  flushing = true
  let sent = 0
  let failed = 0

  try {
    const items = await listQueue(userId)
    const now = Date.now()

    for (const item of items) {
      if (item.status === 'failed') continue
      if (now < nextAttemptAt(item)) continue

      try {
        await api.post('/submissions', buildFormData(item), {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120_000,
        })

        // Accepted — 201 for a first arrival, 200 when the server recognised
        // the idempotency key from an earlier attempt whose response was lost.
        await removeFromQueue(item.id)
        sent++
      } catch (error) {
        const attempts = item.attempts + 1

        /*
         * A validation error is permanent: the data is wrong, and retrying it
         * forever would keep a broken item in the queue and hide the real
         * problem from the worker. Anything else — offline, timeout, 5xx — is
         * worth retrying.
         */
        const permanent =
          error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429

        await update({
          ...item,
          attempts,
          lastAttemptAt: Date.now(),
          lastError: error instanceof Error ? error.message : 'Nepoznata greška',
          status: permanent ? 'failed' : 'pending',
        })

        if (permanent) failed++

        // Stop the pass on a network failure — if one request could not reach
        // the server, the next will not either, and every attempt costs time
        // and battery.
        if (error instanceof ApiError && error.isOffline) break
      }
    }
  } finally {
    flushing = false
  }

  const remaining = (await listQueue(userId)).length
  return { sent, failed, remaining }
}
